import React, { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import dynamic from 'next/dynamic';

import Typography from '@components/Typography';
import { Row, Col } from '@components/Grid';
import Markdown from '@components/utils/Markdown';
import CustomIcon from '@components/utils/CustomIcon';
import { CreditCartOpportinity } from '@assets/icons';
import { useDevice } from '@contexts/device';
import palette from '@utils/theme/palette';
import Tooltip from '@components/ui/Tooltip/next';

import {
  Wrapper,
  Description,
  Title,
  LogoWrapper,
  Container,
  ShowMoreWrapper,
} from './OpportunityCardStyle';

const OpportunityModal = dynamic(() => import('./OpportunityModal'), {
  ssr: false,
});

export interface Promotions {
  name: string;
  description: string;
  image: string;
}
export interface OpportunityCardProps {
  items: Promotions[];
  isShowMore?: boolean;
  showInstallments?: () => void;
  showKocAilem?: boolean;
  isBankInstallments?: boolean;
  showMoreControl?: boolean;
}

const OpportunityItem = ({
  item,
  showKocAilem,
}: {
  item: Promotions;
  showKocAilem?: boolean;
}) => {
  const content = item.description ? (
    <Typography color='D400' variant='caption2' weight='Light'>
      {item.description}
    </Typography>
  ) : null;

  const renderContent = () => (
    <Wrapper>
      <>
        <LogoWrapper>
          <Image
            alt={item.name}
            src={item.image}
            fill
            style={{ objectFit: 'contain' }}
          />
        </LogoWrapper>
        <Title>{item.name}</Title>
      </>
      <Col>
        <Description>
          <Typography variant='caption3' color='D400'>
            <Markdown text={item.description || ''} />
          </Typography>
        </Description>
      </Col>
    </Wrapper>
  );

  return (
    <>
      {item.name === KOC_AILEM && !showKocAilem ? null : (
        <>
          {content ? (
            <Tooltip
              trigger='hover'
              overlayInnerStyle={{
                lineHeight: 1.43,
                textAlign: 'left',
                width: '250px',
                backgroundColor: palette.white,
                padding: '8px 10px',
                borderRadius: '8px',
              }}
              placement='bottom'
              title={content}
              arrowColor={palette.white}
              getPopupContainer={(triggerNode) => triggerNode}
            >
              {renderContent()}
            </Tooltip>
          ) : (
            renderContent()
          )}
        </>
      )}
    </>
  );
};
const KOC_AILEM = 'Koç Ailem';

const OpportunityCard = ({
  items,
  showInstallments,
  showKocAilem,
  isBankInstallments,
  showMoreControl,
}: OpportunityCardProps) => {
  const { isMobile } = useDevice();

  const kocAilemIndex = items.findIndex((item) => item.name === KOC_AILEM);

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const body = document.body;

    if (showModal) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = 'initial';
    }

    return () => {
      body.style.overflow = 'initial';
    };
  }, [showModal]);
  const expandablePromotionsCount = useMemo(() => {
    const remainingItemCount = 0;

    if (!items || items?.length === 0) return remainingItemCount;

    if (kocAilemIndex < 3 && !showKocAilem) return items.length - 3;
    else if (kocAilemIndex < 3 && showKocAilem) return items.length - 3;
    else if (isBankInstallments && !showMoreControl) return remainingItemCount;
    else if (isBankInstallments && showMoreControl)
      return remainingItemCount + 1;
    else return remainingItemCount;
  }, [items, isBankInstallments, kocAilemIndex, showKocAilem, showMoreControl]);

  return (
    <>
      <Container>
        <Row justify={isMobile ? 'space-between' : 'initial'}>
          {kocAilemIndex < 3 && !showKocAilem ? (
            <>
              <Wrapper isButton={true} onClick={showInstallments}>
                <Tooltip
                  trigger='hover'
                  overlayInnerStyle={{
                    lineHeight: 1.43,
                    textAlign: 'left',
                    width: '250px',
                    backgroundColor: palette.white,
                    padding: '8px 10px',
                    borderRadius: '8px',
                  }}
                  placement='bottom'
                  title={
                    isBankInstallments ? null : (
                      <Typography
                        color='D400'
                        variant='caption2'
                        weight='Light'
                      >
                        Kredi kartı taksit fırsatlarını görmek için tarih
                        giriniz.
                      </Typography>
                    )
                  }
                  arrowColor={palette.white}
                  getPopupContainer={(triggerNode) => triggerNode}
                >
                  <Row justify='center'>
                    {isMobile ? null : (
                      <LogoWrapper>
                        <CustomIcon
                          icon={CreditCartOpportinity}
                          width={140}
                          height={30}
                        />
                      </LogoWrapper>
                    )}

                    <Col>
                      <Typography
                        variant='caption2'
                        weight='Regular'
                        color='D400'
                      >
                        <Title>Kredi Kartı Taksit Seçenekleri</Title>
                      </Typography>
                    </Col>
                  </Row>
                </Tooltip>
              </Wrapper>

              {isMobile && items?.length > 0 ? (
                <Wrapper
                  onClick={() => setShowModal(true)}
                  backgroundColor={palette.BR50}
                  isButton={true}
                >
                  <Col>
                    <Typography
                      variant='caption2'
                      weight='Regular'
                      color='R600'
                    >
                      <Title>Kampanyaları Görüntüle</Title>
                    </Typography>
                  </Col>
                </Wrapper>
              ) : (
                items
                  .slice(0, 3)
                  .map((item) => (
                    <OpportunityItem
                      key={item?.name}
                      item={item}
                      showKocAilem={showKocAilem}
                    />
                  ))
              )}
            </>
          ) : (
            <>
              {isMobile
                ? null
                : items
                    .slice(0, 3)
                    .map((item) => (
                      <>
                        {item.name === KOC_AILEM && !showKocAilem ? null : (
                          <OpportunityItem
                            key={item?.name}
                            item={item}
                            showKocAilem={showKocAilem}
                          />
                        )}
                      </>
                    ))}

              {isBankInstallments ? (
                <Wrapper onClick={showInstallments}>
                  {isMobile ? null : (
                    <LogoWrapper>
                      <CustomIcon
                        icon={CreditCartOpportinity}
                        width={140}
                        height={30}
                      />
                    </LogoWrapper>
                  )}
                  <Col>
                    <Title>Kredi Kartı Taksitleri</Title>
                  </Col>
                </Wrapper>
              ) : null}
              {isMobile && items?.length > 0 ? (
                <Wrapper
                  onClick={() => setShowModal(true)}
                  backgroundColor={palette.BR50}
                  isButton={true}
                >
                  <Col>
                    <Typography
                      variant='caption2'
                      weight='Regular'
                      color='R600'
                    >
                      <Title>Kampanyaları Görüntüle</Title>
                    </Typography>
                  </Col>
                </Wrapper>
              ) : null}
            </>
          )}
          {expandablePromotionsCount > 0 && !isMobile ? (
            <ShowMoreWrapper onClick={() => setShowModal(true)}>
              <Typography color='R600' variant='caption1' weight='Bold'>
                +{expandablePromotionsCount}
              </Typography>
              <Typography color='R600' variant='caption2' weight='Regular'>
                Kampanyaları Görüntüle
              </Typography>
            </ShowMoreWrapper>
          ) : null}
        </Row>
      </Container>

      {showModal ? (
        <OpportunityModal items={items} onCancel={() => setShowModal(false)} />
      ) : null}
    </>
  );
};

export default OpportunityCard;
